import { useCallback } from 'react';

import { HappyMomentsHandler } from 'Services/HappyMomentsHandler';
import {
  increaseFollowedAuthors,
  decreaseFollowedAuthors,
} from 'Services/User/marketing/store';

import { FUNCTIONS_MAP_BY_USER_TYPE, USER_TYPE } from './utils';

type OptionsT = {
  userId: string;
  isAuthor: boolean;
};

type UseToggleFollowUserT = (
  userType?: USER_TYPE.User | USER_TYPE.WallStreetBreakfast,
) => {
  follow: (arg1: OptionsT) => Promise<{ rta: boolean }>;
  unfollow: (arg1: OptionsT) => Promise<void>;
  toggleFollow: (
    arg1: OptionsT & {
      isFollowing: boolean;
    },
  ) => Promise<void>;
};

export const useToggleFollow: UseToggleFollowUserT = (
  userType = USER_TYPE.User,
) => {
  const { subscribe, unsubscribe } = FUNCTIONS_MAP_BY_USER_TYPE[userType];

  const follow = useCallback(
    // @ts-expect-error ImplicitAny
    async ({ userId, isAuthor }) => {
      const response = await subscribe(userId);

      HappyMomentsHandler.followAuthor();

      if (isAuthor) {
        increaseFollowedAuthors();
      }

      if ('meta' in response) {
        const { meta: { notifications = { rta: false } } = {} } = response;

        return notifications;
      }

      return { rta: false };
    },
    [subscribe],
  );

  const unfollow = useCallback(
    // @ts-expect-error ImplicitAny
    async ({ userId, isAuthor }) => {
      await unsubscribe(userId);

      if (isAuthor) {
        decreaseFollowedAuthors();
      }
    },
    [unsubscribe],
  );

  const toggleFollow = useCallback(
    // @ts-expect-error ImplicitAny
    async ({ userId, isAuthor, isFollowing }) => {
      const toggle = isFollowing ? unfollow : follow;

      await toggle({
        userId,
        isAuthor,
      });
    },
    [follow, unfollow],
  );

  return {
    follow,
    unfollow,
    toggleFollow,
  };
};
